import { motion, useReducedMotion } from "framer-motion";
import { memo } from "react";
import { Marker } from "react-map-gl/maplibre";
import type { ClusterFeature, PointFeature } from "supercluster";
import { cn } from "@/lib/cn";

interface VenueMarkersProps {
  clusters: Array<
    ClusterFeature<GeoJSON.GeoJsonProperties> | PointFeature<GeoJSON.GeoJsonProperties>
  >;
  hoveredVenueId: string | null;
  selectedVenueId: string | null;
  onHoverMarker: (id: string | null) => void;
  onMarkerClick?: (id: string) => void;
  onClusterClick: (clusterId: number, lat: number, lng: number) => void;
}

const ClusterMarker = memo(
  function ClusterMarker({
    cluster,
    onClusterClick,
  }: {
    cluster: ClusterFeature<GeoJSON.GeoJsonProperties>;
    onClusterClick: (clusterId: number, lat: number, lng: number) => void;
  }) {
    const [lng, lat] = cluster.geometry.coordinates as [number, number];
    const pointCount = cluster.properties.point_count;
    const size = Math.min(24 + pointCount * 2, 56);

    return (
      <Marker longitude={lng} latitude={lat}>
        <button
          type="button"
          aria-label={`View ${pointCount} salons`}
          className="flex items-center justify-center rounded-full bg-graphite text-white shadow-soft2 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric focus-visible:ring-offset-2"
          style={{ width: size, height: size }}
          onClick={() => onClusterClick(cluster.properties.cluster_id, lat, lng)}
        >
          <span className="text-[12px] font-semibold">{pointCount}</span>
        </button>
      </Marker>
    );
  },
  (prev, next) =>
    prev.cluster.properties.cluster_id === next.cluster.properties.cluster_id &&
    prev.cluster.properties.point_count === next.cluster.properties.point_count,
);

// fallow-ignore-next-line complexity
const VenueMarker = memo(
  function VenueMarker({
    feature,
    isHighlighted,
    onHoverMarker,
    onMarkerClick,
  }: {
    feature: PointFeature<GeoJSON.GeoJsonProperties>;
    isHighlighted: boolean;
    onHoverMarker: (id: string | null) => void;
    onMarkerClick?: (id: string) => void;
  }) {
    const prefersReducedMotion = useReducedMotion();
    const [lng, lat] = feature.geometry.coordinates as [number, number];
    const id = feature.properties?.id as string;
    const name = (feature.properties?.name as string) ?? "";
    const displayName = name.length > 16 ? `${name.slice(0, 14)}…` : name;

    return (
      <Marker longitude={lng} latitude={lat}>
        <motion.button
          type="button"
          aria-label={`View ${name}`}
          className={cn(
            "cursor-pointer rounded-pill px-2.5 py-1 text-[11px] font-semibold shadow-soft2 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric focus-visible:ring-offset-2",
            isHighlighted ? "bg-electric-700 text-white" : "bg-graphite text-white",
          )}
          animate={{ scale: isHighlighted ? 1.2 : 1 }}
          transition={
            prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 400, damping: 25 }
          }
          onMouseEnter={() => onHoverMarker(id)}
          onMouseLeave={(event) => {
            if (event.currentTarget !== document.activeElement) onHoverMarker(null);
          }}
          onFocus={() => onHoverMarker(id)}
          onBlur={() => onHoverMarker(null)}
          onClick={() => onMarkerClick?.(id)}
        >
          {displayName}
        </motion.button>
      </Marker>
    );
  },
  (prev, next) =>
    prev.feature.properties?.id === next.feature.properties?.id &&
    prev.isHighlighted === next.isHighlighted &&
    prev.feature.geometry.coordinates[0] === next.feature.geometry.coordinates[0] &&
    prev.feature.geometry.coordinates[1] === next.feature.geometry.coordinates[1],
);

function VenueMarkers({
  clusters,
  hoveredVenueId,
  selectedVenueId,
  onHoverMarker,
  onMarkerClick,
  onClusterClick,
}: VenueMarkersProps) {
  return (
    <>
      {/* fallow-ignore-next-line complexity */}
      {clusters.map((feature) => {
        const isCluster = Boolean(
          feature.properties && "cluster" in feature.properties && feature.properties.cluster,
        );
        const key = isCluster
          ? `cluster-${(feature.properties as { cluster_id: number }).cluster_id}`
          : `point-${(feature.properties as { id: string }).id}`;

        if (isCluster) {
          return (
            <ClusterMarker
              key={key}
              cluster={feature as ClusterFeature<GeoJSON.GeoJsonProperties>}
              onClusterClick={onClusterClick}
            />
          );
        }

        const pointFeature = feature as PointFeature<GeoJSON.GeoJsonProperties>;
        const venueId = pointFeature.properties?.id as string;

        return (
          <VenueMarker
            key={key}
            feature={pointFeature}
            isHighlighted={hoveredVenueId === venueId || selectedVenueId === venueId}
            onHoverMarker={onHoverMarker}
            onMarkerClick={onMarkerClick}
          />
        );
      })}
    </>
  );
}

export { VenueMarkers };
